import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_app/privacy-policy")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-foreground mb-8">
        Privacy Policy
      </h1>

      <div className="prose prose-lg max-w-none dark:prose-invert">
        <p className="text-muted-foreground mb-6">
          <strong>Last updated:</strong> {new Date().toLocaleDateString()}
        </p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            1. Introduction
          </h2>
          <p className="text-muted-foreground mb-4">
            EmojiChess ("we," "our," or "us") is committed to protecting your
            privacy. This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use our chess platform
            service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            2. Information We Collect
          </h2>

          <h3 className="text-xl font-medium text-foreground mb-3">
            2.1 Personal Information
          </h3>
          <p className="text-muted-foreground mb-4">
            We may collect the following personal information:
          </p>
          <ul className="list-disc pl-6 text-muted-foreground mb-4">
            <li>Name and email address (when you create an account)</li>
            <li>Profile information and preferences</li>
            <li>
              Authentication data from third-party providers (Google, etc.)
            </li>
            <li>Communication data when you contact us</li>
          </ul>

          <h3 className="text-xl font-medium text-foreground mb-3">
            2.2 Usage Information
          </h3>
          <p className="text-muted-foreground mb-4">
            We automatically collect certain information, including:
          </p>
          <ul className="list-disc pl-6 text-muted-foreground mb-4">
            <li>Game statistics and performance data</li>
            <li>Device information and browser type</li>
            <li>IP address and location data</li>
            <li>Usage patterns and interactions with our service</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            3. How We Use Your Information
          </h2>
          <p className="text-muted-foreground mb-4">
            We use the collected information for:
          </p>
          <ul className="list-disc pl-6 text-muted-foreground mb-4">
            <li>Providing and maintaining our chess platform service</li>
            <li>Creating and managing user accounts</li>
            <li>Facilitating gameplay and matchmaking</li>
            <li>Tracking game statistics and player rankings</li>
            <li>Improving our service and user experience</li>
            <li>Communicating with users about updates and support</li>
            <li>Ensuring security and preventing fraud</li>
            <li>Complying with legal obligations</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            4. Information Sharing and Disclosure
          </h2>
          <p className="text-muted-foreground mb-4">
            We do not sell, trade, or rent your personal information. We may
            share information in the following circumstances:
          </p>
          <ul className="list-disc pl-6 text-muted-foreground mb-4">
            <li>With your explicit consent</li>
            <li>To comply with legal obligations or court orders</li>
            <li>To protect our rights, property, or safety</li>
            <li>With service providers who assist in operating our platform</li>
            <li>In connection with a business transfer or merger</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            5. Third-Party Services
          </h2>
          <p className="text-muted-foreground mb-4">
            Our service may integrate with third-party authentication providers
            (such as Google) and other services. These third parties have their
            own privacy policies, and we encourage you to review them.
          </p>

          <h3 className="text-xl font-medium text-foreground mb-3">
            5.1 Google OAuth
          </h3>
          <p className="text-muted-foreground mb-4">
            When you sign in with Google, we receive limited profile information
            as authorized by you. We only use this information for
            authentication and account creation purposes.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            6. Data Security
          </h2>
          <p className="text-muted-foreground mb-4">
            We implement appropriate technical and organizational security
            measures to protect your personal information against unauthorized
            access, alteration, disclosure, or destruction. However, no method
            of transmission over the Internet is 100% secure.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            7. Data Retention
          </h2>
          <p className="text-muted-foreground mb-4">
            We retain your personal information for as long as necessary to
            provide our services and fulfill the purposes outlined in this
            Privacy Policy, unless a longer retention period is required by law.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            8. Your Rights
          </h2>
          <p className="text-muted-foreground mb-4">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="list-disc pl-6 text-muted-foreground mb-4">
            <li>Access to your personal information</li>
            <li>Correction of inaccurate data</li>
            <li>Deletion of your personal information</li>
            <li>Restriction of processing</li>
            <li>Data portability</li>
            <li>Objection to processing</li>
            <li>Withdrawal of consent</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            9. Cookies and Tracking
          </h2>
          <p className="text-muted-foreground mb-4">
            We use cookies and similar technologies to enhance your experience,
            analyze usage patterns, and provide personalized content. You can
            control cookie settings through your browser preferences.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            10. Children's Privacy
          </h2>
          <p className="text-muted-foreground mb-4">
            Our service is not intended for children under 13 years of age. We
            do not knowingly collect personal information from children under
            13. If we become aware that we have collected such information, we
            will take steps to delete it.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            11. International Data Transfers
          </h2>
          <p className="text-muted-foreground mb-4">
            Your information may be transferred to and processed in countries
            other than your own. We ensure appropriate safeguards are in place
            to protect your personal information during such transfers.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            12. Changes to This Privacy Policy
          </h2>
          <p className="text-muted-foreground mb-4">
            We may update this Privacy Policy from time to time. We will notify
            you of any material changes by posting the new Privacy Policy on
            this page and updating the "Last updated" date.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            13. Contact Us
          </h2>
          <p className="text-muted-foreground mb-4">
            If you have any questions about this Privacy Policy or our data
            practices, please contact us at:
          </p>
          <p className="text-muted-foreground">
            Email: privacy@emojichess.com
            <br />
            Address: 382 NE 191st St PMB 92929 Miami, Florida
            <br />
            Phone: 7866648873
          </p>
        </section>
      </div>
    </div>
  );
}

